import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { collection, getCountFromServer, getDocs, query, where } from "firebase/firestore";
import { auth, db } from "../../firebase";
import Window from "../../components/Window";
import Icon, { IconTile } from "../../components/Icon";
import { PostComposer, Postcard, toPlainText } from "../../components/Postcards";
import { Thread, repliesCol, roomById, startThread } from "../../rooms";
import { getUsername } from "../../penpal";
import { useBlocks } from "../../blocks";
import "../../styles.css";

// One room, laid out like the PenPal Board: write a post, or open one.
export default function Room() {
  const { roomId } = useParams();
  const room = roomById(roomId);
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const { ids: blocked } = useBlocks();
  const [posts, setPosts] = useState<Thread[] | null>(null);
  const [comments, setComments] = useState<Record<string, number>>({});
  const [search, setSearch] = useState("");
  // ?topic=... opens the room on that topic; &write=1 opens the post form too
  const [params] = useSearchParams();
  const linkedTopic = room?.topics.includes(params.get("topic") || "") ? params.get("topic")! : "";
  const [topic, setTopic] = useState(linkedTopic);
  const [username, setUsername] = useState("");

  useEffect(() => {
    if (user) getUsername(user.uid).then(setUsername).catch(() => setUsername("unknown"));
  }, [user]);

  const load = useCallback(() => {
    if (!room) return;
    getDocs(query(collection(db, "roomThreads"), where("room", "==", room.id)))
      .then((snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as Omit<Thread, "id">) }))
          .filter((t) => !t.removed)
          .sort((a, b) => (b.lastReplyAt?.toMillis() ?? 0) - (a.lastReplyAt?.toMillis() ?? 0));
        setPosts(list);
        // comment counts for the postcards
        list.forEach((t) =>
          getCountFromServer(repliesCol(t.id))
            .then((c) => setComments((prev) => ({ ...prev, [t.id]: c.data().count })))
            .catch(() => undefined)
        );
      })
      .catch((err) => {
        console.error(err);
        setPosts([]);
      });
  }, [room]);

  useEffect(load, [load]);

  if (!room) return <Navigate to="/community" replace />;

  const visible = (posts || []).filter((t) => !(t.uid && blocked.has(t.uid)));
  const term = search.trim().toLowerCase();
  const shown = visible.filter(
    (t) =>
      (!topic || t.categories?.includes(topic)) &&
      (!term ||
        [t.title, toPlainText(t.body), t.anonymous ? "" : t.username, ...(t.categories || [])]
          .join(" ")
          .toLowerCase()
          .includes(term))
  );
  const filtering = !!term || !!topic;

  return (
    <>
      <Window title={`${room.id}.room`} tone="mint">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="window-heading flex items-center gap-3">
              <IconTile name="community" tone="green" size={44} /> {room.title}
            </h1>
            <p className="window-sub !mb-0">{room.description}</p>
          </div>
          <Link to="/community" className="btn btn-plain btn-sm">
            <Icon name="back" size={16} /> All rooms
          </Link>
        </div>
      </Window>

      {user && (
        <Window title="write_a_post.exe" tone="blue">
          <details open={params.get("write") === "1"}>
            <summary className="btn btn-blue cursor-pointer list-none">
              <Icon name="edit" size={16} /> Post in {room.title}
            </summary>
            {username === "unknown" ? (
              <p className="notice mt-4 text-sm">
                Choose a username on <Link to="/profile">My Profile</Link> first, then you can post here.
              </p>
            ) : (
            <PostComposer
              titleLabel="Post title"
              titlePlaceholder={room.titlePlaceholder || "e.g. What's a grounding tool that actually works for you?"}
              bodyPlaceholder={room.bodyPlaceholder || "Share what's on your mind. Others can comment underneath."}
              categoryOptions={room.topics}
              initialCategories={linkedTopic ? [linkedTopic] : undefined}
              ideas={room.prompts}
              anonymousHint="Your username won't appear on this post. Others see 'anonymous'; our moderators can still see who wrote it if it's reported. Anonymous posts can't receive kudos."
              submitLabel="Post it"
              onPost={async (p) => {
                const id = await startThread(user.uid, username || (await getUsername(user.uid)), room.id, p);
                navigate(`/community/thread/${id}`);
              }}
            />
            )}
          </details>
        </Window>
      )}

      {posts !== null && visible.length > 0 && (
        <div className="flex flex-wrap items-end gap-3" role="search">
          <label className="field min-w-[12rem] flex-1">
            <span className="field-label">Search this room</span>
            <input
              type="search"
              className="input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="a word, a topic, a username..."
            />
          </label>
          <label className="field min-w-[12rem]">
            <span className="field-label">Topic</span>
            <select className="input" value={topic} onChange={(e) => setTopic(e.target.value)}>
              <option value="">All topics</option>
              {room.topics.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          {filtering && (
            <button
              type="button"
              className="btn btn-plain btn-sm"
              onClick={() => {
                setSearch("");
                setTopic("");
              }}
            >
              Clear
            </button>
          )}
          {filtering && (
            <p className="w-full text-sm text-ink-soft" role="status">
              {shown.length} of {visible.length} posts
            </p>
          )}
        </div>
      )}

      {posts === null ? (
        <p className="empty">Pulling up chairs...</p>
      ) : filtering && shown.length === 0 ? (
        <div className="window">
          <p className="empty">Nothing matches that yet. Try another word or topic?</p>
        </div>
      ) : visible.length === 0 ? (
        <div className="window">
          <p className="empty">
            <span className="empty-icon">
              <Icon name="mail" size={36} tone="pink" pop />
            </span>
            Nobody has posted here yet. Be the first!
          </p>
        </div>
      ) : (
        <div className="grid gap-5 pt-2 sm:grid-cols-2 xl:grid-cols-3">
          {shown.map((t) => (
            <Postcard
              key={t.id}
              to={`/community/thread/${t.id}`}
              subject={t.title}
              message={t.body}
              cw={t.cw}
              color={t.color}
              categories={t.categories}
              from={t.anonymous ? "— anonymous ✿" : `— @${t.username}`}
              footnote={
                comments[t.id] ? `${comments[t.id]} comment${comments[t.id] === 1 ? "" : "s"}` : undefined
              }
            />
          ))}
        </div>
      )}
    </>
  );
}
